/** @jsx React.createElement */
import * as React from 'react';
import {
  Component,
  createContext,
  createElement,
  createRef,
  CSSProperties,
  forwardRef,
  PropsWithChildren,
  PureComponent,
  ReactElement,
  ReactNode,
  RefObject,
} from 'react';
import {AttrSchema, Component as PanelComponent} from 'panel';
import kebabCase from 'lodash/kebabCase';
import camelCase from 'lodash/camelCase';
import WebComponent from 'webcomponent';

import {Subscribable} from 'common/types/context/subscribable';

type Options = {
  forceProperty: string[];
  forceAttribute: string[];
  forceEvent: string[];
};

type RefType = PanelComponent<any> | HTMLElement;

/**
 * customize fork from https://github.com/BBKolton/reactify-wc/blob/master/src/index.ts
 * we support panel params usage and attrSchema declaration
 *
 * @condition one of following:
 *  1. customElement.define(tagName, component)
 *  2. defineAndMakeJsxFactory(tagName, component)
 *  must be called before using this function
 *
 * @example
 * const MyComponent = wc2react('my-component', Button);
 */
// eslint-disable-next-line sonarjs/cognitive-complexity
function wc2react<Props>(
  tagName: string,
  component: typeof PanelComponent | typeof WebComponent,
  {forceProperty = [], forceAttribute = [], forceEvent = []}: Options = {
    forceProperty: [],
    forceAttribute: [],
    forceEvent: [],
  },
) {
  // make attr and params set available static time to avoid runtime cost
  const attrs = (component as typeof PanelComponent).attrsSchema
    ? new Set(Object.keys((component as typeof PanelComponent).attrsSchema))
    : new Set<string>();
  const params = (component as typeof PanelComponent).prototype.config?.params
    ? new Set(Object.keys((component as typeof PanelComponent).prototype.config.params))
    : new Set<string>();

  let defaultProps = {};
  if ((component as typeof PanelComponent).prototype.config?.params) {
    const defaultParams = (component as typeof PanelComponent).prototype.config.defaultParams || {};
    defaultProps = {...defaultProps, ...defaultParams};
  }
  if ((component as typeof PanelComponent).attrsSchema) {
    const attrsSchema = (component as typeof PanelComponent).attrsSchema;
    const defaultAttrs = Object.fromEntries(
      Object.keys(attrsSchema)
        .filter((key) => typeof attrsSchema[key] !== `string`)
        .filter((key) => (attrsSchema[key] as AttrSchema).default)
        .map((key) => [camelCase(key), (attrsSchema[key] as AttrSchema).default]),
    );
    defaultProps = {...defaultProps, ...defaultAttrs};
  }
  // eslint-disable-next-line local-rules/importable-component
  class Reactified extends Component {
    props: PropsWithChildren<Props> & {
      innerRef?: RefObject<RefType>;
      style?: CSSProperties;
    };
    eventHandlers: Array<[string, (...args: any[]) => any]>;
    attrs: Set<string>;
    params: Set<string>;
    ref: RefObject<RefType>;

    static displayName = `${tagName}-wrapper`;

    constructor(props) {
      super(props);
      this.eventHandlers = [];
      this.ref = this.props.innerRef || createRef<RefType>();
      this.attrs = attrs;
      this.params = params;
    }

    componentDidMount() {
      this.update();
    }

    componentDidUpdate() {
      this.update();
    }

    componentWillUnmount() {
      this.clearEventHandlers();
    }

    setProperty(prop: string, val: any) {
      this.ref.current[prop] = val;
    }

    setAttribute(prop: string, val: string | boolean | number) {
      if (val === false) return this.ref.current.removeAttribute(prop);
      this.ref.current.setAttribute(prop, val?.toString());
    }

    setEvent(event: string, val: (...args: any[]) => any) {
      this.eventHandlers.push([event, val]);
      this.ref.current.addEventListener(event, val as EventListener);
    }

    setParams(params) {
      if (this.ref.current instanceof PanelComponent && this.ref.current.setParams) {
        this.ref.current.setParams(params);
      }
    }

    // eslint-disable-next-line sonarjs/cognitive-complexity
    update() {
      this.clearEventHandlers();
      const params = {};
      Object.entries(this.props).forEach(([prop, val]: [string, any]) => {
        // Check to see if we're forcing the value into a type, and don't
        // proceed if we force
        let forced = false;
        if (forceProperty.includes(prop)) {
          this.setProperty(prop, val);
          forced = true;
        }
        if (forceAttribute.includes(prop)) {
          this.setAttribute(prop, val);
          forced = true;
        }
        if (forceEvent.includes(prop)) {
          this.setEvent(prop, val);
          forced = true;
        }
        if (forced) return;
        if (prop === `style`) return;
        // We haven't forced the type, so determine the correct typing and
        // assign the value to the right place
        if (prop === `children`) {
          return;
        }
        if (prop.toLowerCase() === `classname`) {
          return (this.ref.current.className = val as string);
        }
        if (this.params.has(prop)) {
          params[prop] = val;
          return;
        }
        if (this.attrs.has(kebabCase(prop)) || prop === `style-override` || prop === `styleOverride`) {
          this.setAttribute(kebabCase(prop), typeof val === `object` ? JSON.stringify(val) : val);
          return;
        }
        if (typeof val === `function` && prop.match(/^on[A-Z]/)) {
          return this.setEvent(prop[2].toLowerCase() + prop.slice(3), val);
        }
        if (component.observedAttributes?.includes(kebabCase(prop))) {
          this.setAttribute(kebabCase(prop), typeof val === `object` ? JSON.stringify(val) : val);
          return;
        }
        this.setProperty(prop, val);
      });
      this.setParams(params);
    }

    clearEventHandlers() {
      this.eventHandlers.forEach(([event, handler]) => {
        this.ref.current.removeEventListener(event, handler as EventListener);
      });
      this.eventHandlers = [];
    }

    render() {
      const {children, style} = this.props;
      return createElement(tagName, {ref: this.ref, style}, children);
    }
  }

  const Comp = forwardRef<{innerRef?: RefObject<RefType>}, PropsWithChildren<Props> & {style?: CSSProperties}>(
    ({children, ...props}, ref) => createElement(Reactified, {innerRef: ref, ...props}, children),
  );
  Comp.displayName = `${tagName}-forward-ref`;
  Comp.defaultProps = defaultProps;
  return Comp;
}

export function contextify<S extends Subscribable>() {
  const context = createContext<S>(null);
  class Provider extends PureComponent<{children: ReactNode; value: S}> {
    componentDidMount() {
      this.props.value.subscribe(this.update);
    }

    componentWillUnmount() {
      this.props.value.unsubscribe(this.update);
    }

    update = () => {
      this.forceUpdate();
    };

    render() {
      // create a new Proxy on each render so that the consumed value always triggers a prop update when passed into a PureComponent
      return <context.Provider value={new Proxy(this.props.value, {})}>{this.props.children}</context.Provider>;
    }
  }

  const Consumer = context.Consumer;

  // will restraint using hooks until further notice, we don't want to have multiple standard for the react codebase
  return {
    Provider,
    Consumer,
  };
}

type KebabCaseToCamelCase<S extends string> = S extends `${infer T}-${infer U}`
  ? `${T}${Capitalize<KebabCaseToCamelCase<U>>}`
  : S;

type CamelCaseKeys<T> = {[K in keyof T as KebabCaseToCamelCase<string & K>]: T[K]};

type PrefixEventHandlers<T> = {[K in keyof T as `on${Capitalize<KebabCaseToCamelCase<string & K>>}`]?: T[K]};

type Func = (...args: any[]) => any;

type Props<
  T extends {
    attrs?: Record<string, any>;
    props?: Record<string, any>;
    params?: Record<string, any>;
    on?: Record<string, Func>;
  }
> = CamelCaseKeys<T['attrs']> &
  T['props'] &
  T['params'] &
  PrefixEventHandlers<T['on']> &
  Partial<{
    elref: string;
    'style-override': string;
    style: CSSProperties;
    className: string;
  }>;

type ToReactProps<T> = Props<T> &
  Omit<React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>, keyof Props<T>>;

type ArgumentTypes<F extends Func> = F extends (...args: infer A) => any ? A : never;

/**
 * Convenient helper to omit `ToReactProps` for the generic type definition.
 *
 * @see wc2react for more details
 */
export function panel2react<JsxProps>(...args: ArgumentTypes<typeof wc2react>) {
  return wc2react<ToReactProps<JsxProps>>(...args);
}

// export this for our custom consumer wrappers for hook-base API
export type ConsumerLike<T> = (props: ConsumerProps<T>) => React.ReactElement;
type ConsumerProps<T> = {children: (parameter: T) => React.ReactElement};
type ContextValue<T> = T extends ConsumerLike<infer U> ? U : never;
type ContextValues<T> = {[P in keyof T]: ContextValue<T[P]>};
type UnknownConsumer = ConsumerLike<unknown>;

/**
 * Generic multi-consumer component to enable consuming multiple contexts using consumer syntax
 * @example
 * <Consumers consumers={[CurrentProjectConsumer, BookmarkStoreConsumer] as const}>
 *   {([currentProject, bookmarkStore]) => (
 *      <YourComponent
 *        currentProject={currentProject}
 *        bookmarkStore={bookmarkStore}
 *      />
 *   )}
 * </Consumers>
 */
export const Consumers = <C extends readonly UnknownConsumer[]>({
  consumers,
  contexts,
  children,
}: {
  consumers: C;
  contexts?: ContextValues<C>;
  children: (contexts: ContextValues<C>) => ReactElement;
}) => {
  if (!consumers.length) {
    return children(contexts);
  }
  const ConsumerComp = consumers[0];
  return (
    <ConsumerComp>
      {(context) => (
        <Consumers
          consumers={consumers.slice(1)}
          contexts={[...(contexts || []), context]}
          children={children as any}
        />
      )}
    </ConsumerComp>
  );
};
